import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Search, ChevronLeft, ChevronRight, FileText, Eye, RefreshCw, Database, Edit2, Save, X, ArrowUpDown, ArrowUp, ArrowDown, Filter, FilterX } from 'lucide-react';

// The direction every column uses the first time it is clicked. The document
// number and the dates start with the newest invoice on top, the names and
// the numbers of the customer start with the smallest one.
const SORT_DEFAULT_ORDER = {
  document_no: 'DESC',
  invoice_no: 'DESC',
  posting_date: 'DESC',
  customer_name: 'ASC',
  customer_no: 'ASC',
  amount: 'DESC',
  amount_incl_vat: 'DESC',
  remaining_amount: 'DESC',
  salesperson: 'ASC',
  location: 'ASC',
  shipment_date: 'DESC',
  delivered_date: 'DESC',
  delivery_status: 'ASC',
  due_date: 'DESC',
  aging_days: 'DESC',
  type: 'ASC',
  other_amounts: 'DESC',
  other_remarks: 'ASC'
};

// The columns of the table that can be filtered, with the kind of value they
// hold. The kind decides the operators the user can pick, the same way the
// filter of Business Central works.
const FILTER_COLUMNS = {
  document_no: { label: 'Document No.', type: 'text' },
  invoice_no: { label: 'Invoice No.', type: 'text' },
  posting_date: { label: 'Posting Date', type: 'date' },
  customer_name: { label: 'Customer Name', type: 'text' },
  customer_no: { label: 'Customer No.', type: 'text' },
  amount: { label: 'Amount', type: 'number' },
  amount_incl_vat: { label: 'Amount (Incl. VAT)', type: 'number' },
  remaining_amount: { label: 'Remaining Amount', type: 'number' },
  salesperson: { label: 'Salesperson', type: 'text' },
  location: { label: 'Location', type: 'text' },
  shipment_date: { label: 'Shipment Date (BC)', type: 'date' },
  delivered_date: { label: 'Delivered Date', type: 'date' },
  delivery_status: { label: 'Delivery Status', type: 'text' },
  due_date: { label: 'Due Date', type: 'date' },
  aging_days: { label: 'Aging (Days)', type: 'number' },
  type: { label: 'Type', type: 'text' },
  other_amounts: { label: 'Other Amounts', type: 'text' },
  other_remarks: { label: 'Other Remarks', type: 'text' }
};

// The operators of every kind of value, with the label the user reads.
const FILTER_OPERATORS = {
  text: [
    { value: 'contains', label: 'Contains' },
    { value: 'not_contains', label: 'Does Not Contain' },
    { value: 'begins_with', label: 'Begins With' },
    { value: 'ends_with', label: 'Ends With' },
    { value: 'is', label: 'Is' },
    { value: 'is_not', label: 'Is Not' },
    { value: 'is_empty', label: 'Is Empty' },
    { value: 'is_not_empty', label: 'Is Not Empty' }
  ],
  date: [
    { value: 'on', label: 'Is On' },
    { value: 'on_or_after', label: 'Is On or After' },
    { value: 'on_or_before', label: 'Is On or Before' },
    { value: 'is_empty', label: 'Is Empty' },
    { value: 'is_not_empty', label: 'Is Not Empty' }
  ],
  number: [
    { value: 'equals', label: 'Equals' },
    { value: 'not_equals', label: 'Does Not Equal' },
    { value: 'greater_than', label: 'Is Greater Than' },
    { value: 'less_than', label: 'Is Less Than' },
    { value: 'is_empty', label: 'Is Empty' },
    { value: 'is_not_empty', label: 'Is Not Empty' }
  ]
};

// The operators that do not need a value.
const OPERATORS_WITHOUT_VALUE = ['is_empty', 'is_not_empty'];

// The label of an operator, to show it in the list of the active filters.
function operatorLabel(type, value) {
  const found = (FILTER_OPERATORS[type] || []).find(op => op.value === value);
  return found ? found.label : value;
}

function formatCurrency(val) {
  return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(val || 0);
}

// The other amounts is a free text field, so it can hold anything. A value
// that is a number keeps the look of an amount, everything else is shown the
// way it was typed. The field holds one amount per line, so every line is
// formatted on its own and keeps its own line in the table.
function formatOtherAmounts(val) {
  if (val === null || val === undefined) return '-';
  const text = String(val).trim();
  if (text === '') return '-';
  return text.split('\n').map(line => {
    const trimmed = line.trim();
    if (trimmed === '') return '';
    const num = Number(trimmed);
    if (!isNaN(num)) return formatCurrency(num);
    return trimmed;
  }).join('\n');
}

// A "YYYY-MM-DD" value is a calendar date, not an instant. Building the Date
// from its parts keeps it on the same day in every time zone, while
// `new Date('2026-10-27')` is parsed as UTC midnight and lands on the previous
// day in any negative offset time zone.
function parseCalendarDate(value) {
  if (!value) return null;
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) {
    const parsed = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    return isNaN(parsed.getTime()) ? null : parsed;
  }
  const fallback = new Date(value);
  return isNaN(fallback.getTime()) ? null : fallback;
}

function formatDate(d) {
  if (!d) return '-';
  const parsed = parseCalendarDate(d);
  if (!parsed) return '-';
  return parsed.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function calcAgingDays(shipmentDate) {
  const from = parseCalendarDate(shipmentDate);
  if (!from) return null;
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  from.setHours(0, 0, 0, 0);
  return Math.floor((now - from) / (1000 * 60 * 60 * 24));
}

export default function PostedSalesInvoice() {
  const [invoices, setInvoices] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 15, total: 0, totalPages: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [paidStatus, setPaidStatus] = useState('');
  const [deliveryStatus, setDeliveryStatus] = useState('');
  const [sortBy, setSortBy] = useState('posting_date');
  const [sortOrder, setSortOrder] = useState('DESC');
  const [filters, setFilters] = useState([]);
  const [contextMenu, setContextMenu] = useState(null);
  const [filterDialog, setFilterDialog] = useState(null);
  const [company, setCompany] = useState('');
  const [companies, setCompanies] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [invoiceLines, setInvoiceLines] = useState([]);
  const [linesLoading, setLinesLoading] = useState(false);
  const [showLinesModal, setShowLinesModal] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  const [editModal, setEditModal] = useState(null);
  const [editForm, setEditForm] = useState({ other_type: '', other_amounts: '', other_remarks: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get('/api/bc/companies')
      .then(res => {
        setCompanies(res.data.companies);
        if (res.data.companies.length > 0) {
          setCompany(res.data.companies[0]);
        }
      })
      .catch(err => console.error('Load companies error:', err));
  }, []);

  useEffect(() => {
    axios.get('/api/bc/sync-status')
      .then(res => setSyncStatus(res.data.data))
      .catch(err => console.error('Sync status error:', err));
  }, []);

  const load = useCallback(async (page = 1) => {
    if (!company) return;
    setLoading(true);
    try {
      const res = await axios.get('/api/bc/posted-sales-invoices', {
        params: {
          company, page, limit: 15, search, dateFrom, dateTo, paidStatus,
          deliveryStatus, sortBy, order: sortOrder,
          filters: JSON.stringify(filters)
        }
      });
      setInvoices(res.data.data);
      setPagination(res.data.pagination);
    } catch (err) {
      console.error('Load invoices error:', err);
      setInvoices([]);
    }
    setLoading(false);
  }, [company, search, dateFrom, dateTo, paidStatus, deliveryStatus, sortBy, sortOrder, filters]);

  useEffect(() => { load(); }, [load]);

  // Clicking a column sorts the table by it. The first click uses the
  // direction of the column, the next clicks switch between ascending and
  // descending.
  function handleSort(column) {
    if (sortBy === column) {
      setSortOrder(prev => prev === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortBy(column);
      setSortOrder(SORT_DEFAULT_ORDER[column] || 'ASC');
    }
  }

  // The menu of a column header, the same way Business Central offers it:
  // ascending, descending, clear the sort and the filter of the column.
  function openContextMenu(event, column) {
    event.preventDefault();
    event.stopPropagation();
    setFilterDialog(null);
    setContextMenu({
      column,
      x: Math.min(event.clientX, window.innerWidth - 200),
      y: Math.min(event.clientY, window.innerHeight - 200)
    });
  }

  // The filter of a column. The filter that is already set for the column is
  // taken along, so the user can change it instead of starting over.
  function openFilterDialog(column, anchor, position) {
    setContextMenu(null);
    const existing = filters.find(f => f.column === column);
    const type = FILTER_COLUMNS[column].type;
    const rect = anchor && anchor.getBoundingClientRect
      ? anchor.getBoundingClientRect()
      : position
        ? { left: position.x, bottom: position.y }
        : { left: 100, bottom: 200 };
    setFilterDialog({
      column,
      type,
      operator: existing ? existing.operator : FILTER_OPERATORS[type][0].value,
      value: existing ? existing.value : '',
      x: Math.max(8, Math.min(rect.left, window.innerWidth - 330)),
      y: Math.max(8, Math.min(rect.bottom + 4, window.innerHeight - 300))
    });
  }

  function applyFilter() {
    if (!filterDialog) return;
    const { column, operator, value } = filterDialog;
    const needsValue = !OPERATORS_WITHOUT_VALUE.includes(operator);
    if (needsValue && String(value).trim() === '') {
      // A filter without a value would show an empty table.
      setFilterDialog(null);
      return;
    }
    setFilters(prev => [
      ...prev.filter(f => f.column !== column),
      { column, operator, value: needsValue ? String(value).trim() : '' }
    ]);
    setFilterDialog(null);
  }

  function clearFilter(column) {
    setFilters(prev => prev.filter(f => f.column !== column));
    setFilterDialog(null);
  }

  function clearAllFilters() {
    setFilters([]);
    setFilterDialog(null);
  }

  function sortByColumn(column, direction) {
    setSortBy(column);
    setSortOrder(direction);
    setContextMenu(null);
  }

  // The sort of a column is removed by going back to the posting date, the
  // order the table starts with.
  function clearSort() {
    setSortBy('posting_date');
    setSortOrder('DESC');
    setContextMenu(null);
  }

  function activeFilterFor(column) {
    return filters.find(f => f.column === column) || null;
  }

  function SortIcon({ column }) {
    if (sortBy !== column) return <ArrowUpDown size={12} style={{ opacity: 0.3, marginLeft: 4 }} />;
    return sortOrder === 'ASC'
      ? <ArrowUp size={12} style={{ marginLeft: 4, color: '#3b82f6' }} />
      : <ArrowDown size={12} style={{ marginLeft: 4, color: '#3b82f6' }} />;
  }

  function SortableTh({ column, children, title }) {
    const isActive = sortBy === column;
    const activeFilter = activeFilterFor(column);
    return (
      <th
        onClick={() => handleSort(column)}
        onContextMenu={e => openContextMenu(e, column)}
        title={`${title ? `${title} | ` : ''}Sort by ${children} ${isActive ? (sortOrder === 'ASC' ? '(ascending)' : '(descending)') : ''} | Right click for the filter of the column`}
        style={{
          cursor: 'pointer',
          userSelect: 'none',
          whiteSpace: 'nowrap',
          background: isActive ? '#f0f9ff' : undefined,
          transition: 'background 0.15s'
        }}
        onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = '#f8fafc'; }}
        onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = ''; }}
      >
        <span style={{ display: 'inline-flex', alignItems: 'center' }}>
          {children}
          <SortIcon column={column} />
          <span
            role="button"
            title={activeFilter
              ? `Filter: ${operatorLabel(FILTER_COLUMNS[column].type, activeFilter.operator)}${activeFilter.value ? ` ${activeFilter.value}` : ''} - click to change it`
              : 'Filter this column'}
            onClick={e => { e.stopPropagation(); openFilterDialog(column, e.currentTarget); }}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              marginLeft: 6,
              color: activeFilter ? '#2563eb' : '#94a3b8',
              opacity: activeFilter ? 1 : 0.55
            }}
          >
            {activeFilter ? <Filter size={12} /> : <FilterX size={12} />}
          </span>
        </span>
      </th>
    );
  }

  async function handleSync() {
    if (!company) return;
    setSyncing(true);
    try {
      const res = await axios.post('/api/bc/sync-invoices', { company });
      alert(res.data.message);
      const statusRes = await axios.get('/api/bc/sync-status');
      setSyncStatus(statusRes.data.data);
      load(1);
    } catch (err) {
      alert('Sync error: ' + (err.response?.data?.error || err.message));
    }
    setSyncing(false);
  }

  async function handleViewLines(invoice) {
    setLinesLoading(true);
    setSelectedInvoice(invoice);
    try {
      const res = await axios.get('/api/bc/posted-sales-invoice-lines', {
        params: { company, documentNo: invoice.invoice_no }
      });
      if (res.data.data && res.data.data.length > 0) {
        setInvoiceLines(res.data.data);
      } else {
        const syncRes = await axios.post('/api/bc/sync-invoice-lines', {
          company,
          invoiceNo: invoice.invoice_no
        });
        const dbRes = await axios.get('/api/bc/posted-sales-invoice-lines', {
          params: { company, documentNo: invoice.invoice_no }
        });
        setInvoiceLines(dbRes.data.data);
      }
      setShowLinesModal(true);
    } catch (err) {
      alert('Error loading invoice lines: ' + (err.response?.data?.error || err.message));
    }
    setLinesLoading(false);
  }

  function openEditModal(invoice) {
    setEditModal(invoice);
    setEditForm({
      other_type: invoice.other_type || '',
      other_amounts: invoice.other_amounts || '',
      other_remarks: invoice.other_remarks || ''
    });
  }

  async function handleSaveEdit(e) {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`/api/bc/posted-sales-invoice/${editModal.id}`, {
        other_type: editForm.other_type,
        other_amounts: editForm.other_amounts,
        other_remarks: editForm.other_remarks
      });
      alert('Invoice updated successfully');
      setEditModal(null);
      load(pagination.page);
    } catch (err) {
      alert('Failed to update invoice: ' + (err.response?.data?.error || err.message));
    }
    setSaving(false);
  }

  function getSyncInfo() {
    if (!syncStatus || !company) return null;
    return syncStatus.find(s => s.company === company);
  }

  const syncInfo = getSyncInfo();

  return (
    <div>
      <div className="table-container">
        <div className="table-header table-title-row">
          <h3>Posted Sales Invoices ({pagination.total.toLocaleString()} total)</h3>
        </div>

        <div className="table-filters-row">
          <select className="filter-select" value={company} onChange={e => setCompany(e.target.value)} style={{ minWidth: 200 }}>
            {companies.map(c => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
          <select className="filter-select" value={paidStatus} onChange={e => setPaidStatus(e.target.value)} style={{ minWidth: 140 }}>
            <option value="">All Status</option>
            <option value="paid">Paid</option>
            <option value="unpaid">Unpaid</option>
          </select>
          <select
            className="filter-select"
            value={deliveryStatus}
            onChange={e => setDeliveryStatus(e.target.value)}
            style={{ minWidth: 165 }}
            title="Show only the invoices whose goods were delivered, or only the invoices that are still waiting for their delivery"
          >
            <option value="">All Deliveries</option>
            <option value="delivered">Delivered</option>
            <option value="pending">Pending</option>
          </select>
          <div className="table-search-wrap">
            <Search size={16} className="table-search-icon" />
            <input
              className="search-input table-search-input"
              placeholder="Search document no. or customer..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <label className="table-date-field">
            <span>From</span>
            <input type="date" className="filter-select" value={dateFrom} onChange={e => setDateFrom(e.target.value)} title="Invoices posted on this date or later" />
          </label>
          <label className="table-date-field">
            <span>To</span>
            <input type="date" className="filter-select" value={dateTo} onChange={e => setDateTo(e.target.value)} title="Invoices posted on this date or earlier" />
          </label>
          <button className="btn btn-primary table-filters-end" onClick={handleSync} disabled={syncing || !company} style={{ background: syncing ? '#94a3b8' : '#16a34a', whiteSpace: 'nowrap' }}>
            <RefreshCw size={16} style={{ animation: syncing ? 'spin 1s linear infinite' : 'none' }} /> {syncing ? 'Syncing...' : 'Sync from BC'}
          </button>
        </div>

        {syncInfo && (
          <div style={{ padding: '8px 16px', background: '#f0fdf4', borderBottom: '1px solid #bbf7d0', fontSize: 12, color: '#166534', display: 'flex', alignItems: 'center', gap: 8 }}>
            <Database size={14} />
            <span>Cached: {syncInfo.total_invoices.toLocaleString()} invoices | Last synced: {formatDate(syncInfo.last_sync)}</span>
          </div>
        )}

        {filters.length > 0 && (
          <div style={{ padding: '8px 16px', background: '#eff6ff', borderBottom: '1px solid #bfdbfe', fontSize: 12, color: '#1e40af', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <Filter size={14} />
            <span style={{ fontWeight: 600 }}>Column filters:</span>
            {filters.map(f => (
              <span
                key={f.column}
                title="Click to change this filter"
                onClick={() => openFilterDialog(f.column, null)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '2px 8px',
                  borderRadius: 999,
                  background: '#dbeafe',
                  border: '1px solid #93c5fd',
                  cursor: 'pointer'
                }}
              >
                <span>{FILTER_COLUMNS[f.column].label}</span>
                <span style={{ opacity: 0.75 }}>{operatorLabel(FILTER_COLUMNS[f.column].type, f.operator)}</span>
                {f.value && <strong style={{ fontWeight: 600 }}>{f.value}</strong>}
                <span
                  role="button"
                  title="Remove this filter"
                  onClick={e => { e.stopPropagation(); clearFilter(f.column); }}
                  style={{ display: 'inline-flex', color: '#1d4ed8', cursor: 'pointer' }}
                >
                  <X size={12} />
                </span>
              </span>
            ))}
            <button
              className="btn btn-secondary btn-sm"
              onClick={clearAllFilters}
              title="Remove all the column filters"
              style={{ fontSize: 11, padding: '2px 8px', marginLeft: 4 }}
            >
              Clear all
            </button>
          </div>
        )}

        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <SortableTh column="document_no">Document No.</SortableTh>
                <SortableTh column="invoice_no">Invoice No.</SortableTh>
                <SortableTh column="posting_date">Posting Date</SortableTh>
                <SortableTh column="customer_name">Customer Name</SortableTh>
                <SortableTh column="customer_no">Customer No.</SortableTh>
                <SortableTh column="amount">Amount</SortableTh>
                <SortableTh column="amount_incl_vat">Amount (Incl. VAT)</SortableTh>
                <SortableTh column="remaining_amount">Remaining Amount</SortableTh>
                <SortableTh column="salesperson">Salesperson</SortableTh>
                <SortableTh column="location">Location</SortableTh>
                <SortableTh column="delivered_date" title="Delivered date from the OneDrive workbook (Date Delivered column). The delivery is found by the invoice number, or by the amount incl. VAT and the customer code when the workbook uses another invoice number.">Delivered Date</SortableTh>
                <SortableTh column="delivery_status" title="Delivery status from the OneDrive workbook">Delivery Status</SortableTh>
                <SortableTh column="due_date" title="Due date of the invoice based on the delivered date and the payment terms of the customer (N30 = 30 days after the delivery, COD = due on the delivery). It is empty while the invoice has no delivered date yet. The badge marks a date that differs from the due date of Business Central.">Due Date</SortableTh>
                <SortableTh column="aging_days" title="Days since the delivered date">Aging (Days)</SortableTh>
                <SortableTh column="type">Type</SortableTh>
                <SortableTh column="other_amounts">Other Amounts</SortableTh>
                <SortableTh column="other_remarks">Other Remarks</SortableTh>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={18} style={{ textAlign: 'center', padding: 40 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                    <div style={{ width: 20, height: 20, border: '2px solid #e5e7eb', borderTop: '2px solid #3b82f6', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
                    Loading...
                  </div>
                </td></tr>
              ) : invoices.length === 0 ? (
                <tr><td colSpan={18} style={{ textAlign: 'center', padding: 40 }}>
                  <div>
                    <p style={{ marginBottom: 8 }}>No posted sales invoices found</p>
                    <p style={{ fontSize: 12, color: '#64748b' }}>Click "Sync from BC" to fetch invoices from Business Central</p>
                  </div>
                </td></tr>
              ) : invoices.map((inv, i) => (
                <tr key={inv.id || i}>
                  <td style={{ fontFamily: 'monospace', fontWeight: 600 }}>{inv.invoice_no || '-'}</td>
                  <td style={{ fontFamily: 'monospace' }}>{inv.external_document_no || '-'}</td>
                  <td>{formatDate(inv.posting_date)}</td>
                  <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{inv.sell_to_customer_name || '-'}</td>
                  <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{inv.sell_to_customer_no || '-'}</td>
                  <td className="amount">{formatCurrency(inv.amount)}</td>
                  <td className="amount" style={{ fontWeight: 600 }}>{formatCurrency(inv.amount_including_vat)}</td>
                  <td className="amount" style={{ color: inv.remaining_amount > 0.01 ? '#dc2626' : '#16a34a', fontWeight: 500 }}>
                    {formatCurrency(inv.remaining_amount)}
                  </td>
                  <td>{inv.salesperson_code || '-'}</td>
                  <td>{inv.location_code || '-'}</td>
                  <td>
                    <div
                      style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                      title={inv.delivery_invoice_number ? `Delivery ${inv.delivery_invoice_number}` : undefined}
                    >
                      <span>{formatDate(inv.delivery_date_delivered)}</span>
                      {inv.delivery_match === 'amount' && (
                        <span
                          title={`Matched by amount incl. VAT and customer code${inv.delivery_invoice_number ? ` to delivery ${inv.delivery_invoice_number}` : ''}`}
                          style={{
                            fontSize: 10,
                            fontWeight: 600,
                            padding: '1px 6px',
                            borderRadius: 999,
                            background: '#fef3c7',
                            color: '#92400e',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          by amount
                        </span>
                      )}
                    </div>
                  </td>
                  <td>{inv.delivery_status || '-'}</td>
                  <td>
                    <div
                      style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                      title={(() => {
                        const terms = inv.payment_terms_code
                          ? `${inv.payment_terms_code} (${inv.payment_terms_days} days)`
                          : 'no payment terms';
                        if (inv.due_date_computed) {
                          const base = `Delivered ${formatDate(inv.delivery_date_delivered)} + ${terms} = ${formatDate(inv.due_date_computed)}`;
                          return inv.due_date_shifted
                            ? `${base}\nBusiness Central due date: ${formatDate(inv.due_date)}`
                            : base;
                        }
                        if (inv.delivery_date_delivered) {
                          return `Delivered ${formatDate(inv.delivery_date_delivered)}, but the customer has no payment terms`;
                        }
                        return inv.due_date
                          ? `No delivered date yet - Business Central due date: ${formatDate(inv.due_date)}`
                          : undefined;
                      })()}
                    >
                      <span>{formatDate(inv.due_date_computed)}</span>
                      {inv.due_date_shifted && (
                        <span
                          title={`Shifted because the goods were delivered later than the invoice was posted. Business Central due date: ${formatDate(inv.due_date)}`}
                          style={{
                            fontSize: 10,
                            fontWeight: 600,
                            padding: '1px 6px',
                            borderRadius: 999,
                            background: '#dbeafe',
                            color: '#1d4ed8',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          from delivery
                        </span>
                      )}
                    </div>
                  </td>
                  <td style={{ textAlign: 'center', fontWeight: 500 }}>
                    {(() => {
                      const agingDays = inv.delivery_aging_days !== null && inv.delivery_aging_days !== undefined ? inv.delivery_aging_days : calcAgingDays(inv.delivery_date_delivered);
                      if (agingDays === null || agingDays === undefined) return '-';
                      const color = agingDays > 30 ? '#dc2626' : agingDays > 15 ? '#f59e0b' : '#16a34a';
                      return <span style={{ color }}>{agingDays} days</span>;
                    })()}
                  </td>
                  <td style={{ maxWidth: 200, whiteSpace: 'pre-wrap', wordBreak: 'break-word', verticalAlign: 'top', lineHeight: 1.4 }} title={inv.other_type || ''}>{inv.other_type || '-'}</td>
                  <td className="amount" style={{ maxWidth: 200, verticalAlign: 'top', whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.4 }}>{formatOtherAmounts(inv.other_amounts)}</td>
                  <td style={{ maxWidth: 200, whiteSpace: 'pre-wrap', wordBreak: 'break-word', verticalAlign: 'top', lineHeight: 1.4 }}>{inv.other_remarks || '-'}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => openEditModal(inv)}
                        title="Edit Invoice"
                      >
                        <Edit2 size={14} />
                      </button>
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => handleViewLines(inv)}
                        disabled={linesLoading}
                        title="View Invoice Lines"
                      >
                        <Eye size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* The menu of a column header, the same way Business Central offers it */}
      {contextMenu && (
        <>
          <div
            style={{ position: 'fixed', inset: 0, zIndex: 60 }}
            onClick={() => setContextMenu(null)}
            onContextMenu={e => { e.preventDefault(); setContextMenu(null); }}
          />
          <div
            style={{
              position: 'fixed',
              left: contextMenu.x,
              top: contextMenu.y,
              zIndex: 61,
              minWidth: 190,
              background: '#fff',
              border: '1px solid #e2e8f0',
              borderRadius: 8,
              boxShadow: '0 10px 30px rgba(15, 23, 42, 0.18)',
              padding: 4,
              fontSize: 13
            }}
          >
            {[
              { label: 'Ascending', icon: ArrowUp, action: () => sortByColumn(contextMenu.column, 'ASC') },
              { label: 'Descending', icon: ArrowDown, action: () => sortByColumn(contextMenu.column, 'DESC') },
              { label: 'Clear Sort', icon: ArrowUpDown, action: clearSort }
            ].map(item => (
              <button
                key={item.label}
                onClick={item.action}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  width: '100%',
                  padding: '7px 10px',
                  border: 'none',
                  background: 'transparent',
                  borderRadius: 6,
                  cursor: 'pointer',
                  textAlign: 'left',
                  color: '#0f172a'
                }}
                onMouseEnter={e => { e.currentTarget.style.background = '#f1f5f9'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
              >
                <item.icon size={14} />
                {item.label}
              </button>
            ))}
            <div style={{ height: 1, background: '#e2e8f0', margin: '4px 0' }} />
            <button
              onClick={() => openFilterDialog(contextMenu.column, null, contextMenu)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                width: '100%',
                padding: '7px 10px',
                border: 'none',
                background: 'transparent',
                borderRadius: 6,
                cursor: 'pointer',
                textAlign: 'left',
                color: '#0f172a'
              }}
              onMouseEnter={e => { e.currentTarget.style.background = '#f1f5f9'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
            >
              <Filter size={14} />
              Filter...
            </button>
          </div>
        </>
      )}

      {/* The filter of a single column */}
      {filterDialog && (
        <>
          <div
            style={{ position: 'fixed', inset: 0, zIndex: 60 }}
            onClick={() => setFilterDialog(null)}
          />
          <div
            style={{
              position: 'fixed',
              left: filterDialog.x,
              top: filterDialog.y,
              zIndex: 61,
              width: 300,
              background: '#fff',
              border: '1px solid #e2e8f0',
              borderRadius: 8,
              boxShadow: '0 10px 30px rgba(15, 23, 42, 0.18)',
              padding: 14,
              fontSize: 13
            }}
          >
            <div style={{ fontWeight: 600, marginBottom: 10, color: '#0f172a' }}>
              Filter: {FILTER_COLUMNS[filterDialog.column].label}
            </div>
            <select
              className="filter-select"
              value={filterDialog.operator}
              onChange={e => setFilterDialog(prev => ({ ...prev, operator: e.target.value }))}
              style={{ width: '100%', marginBottom: 10 }}
              autoFocus
            >
              {FILTER_OPERATORS[filterDialog.type].map(op => (
                <option key={op.value} value={op.value}>{op.label}</option>
              ))}
            </select>
            {!OPERATORS_WITHOUT_VALUE.includes(filterDialog.operator) && (
              filterDialog.type === 'date' ? (
                <input
                  type="date"
                  value={filterDialog.value}
                  onChange={e => setFilterDialog(prev => ({ ...prev, value: e.target.value }))}
                  style={{ width: '100%', marginBottom: 10, padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }}
                />
              ) : (
                <input
                  type={filterDialog.type === 'number' ? 'number' : 'text'}
                  value={filterDialog.value}
                  onChange={e => setFilterDialog(prev => ({ ...prev, value: e.target.value }))}
                  placeholder={filterDialog.type === 'number' ? 'Value' : 'Value'}
                  style={{ width: '100%', marginBottom: 10, padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }}
                  onKeyDown={e => { if (e.key === 'Enter') applyFilter(); }}
                />
              )
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              {activeFilterFor(filterDialog.column) && (
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => clearFilter(filterDialog.column)}
                  style={{ marginRight: 'auto', fontSize: 11, padding: '4px 8px' }}
                >
                  Clear Filter
                </button>
              )}
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => setFilterDialog(null)}
                style={{ fontSize: 11, padding: '4px 10px' }}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary btn-sm"
                onClick={applyFilter}
                style={{ fontSize: 11, padding: '4px 12px', background: '#2563eb' }}
              >
                Apply
              </button>
            </div>
          </div>
        </>
      )}

      <div className="pagination">
        <div className="pagination-info">
          Page {pagination.page} of {pagination.totalPages} ({pagination.total.toLocaleString()} records)
        </div>
        <div className="pagination-buttons">
          <button className="page-btn" disabled={pagination.page <= 1} onClick={() => load(1)}>First</button>
          <button className="page-btn" disabled={pagination.page <= 1} onClick={() => load(pagination.page - 1)}><ChevronLeft size={16} /></button>
          <button className="page-btn" disabled={pagination.page >= pagination.totalPages} onClick={() => load(pagination.page + 1)}><ChevronRight size={16} /></button>
          <button className="page-btn" disabled={pagination.page >= pagination.totalPages} onClick={() => load(pagination.totalPages)}>Last</button>
        </div>
      </div>

      {/* Invoice Lines Modal */}
      {showLinesModal && selectedInvoice && (
        <div className="modal-overlay" onClick={() => setShowLinesModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 900, maxHeight: '80vh', overflow: 'auto' }}>
            <div className="modal-header">
              <h3><FileText size={20} /> Invoice Lines - {selectedInvoice.invoice_no}</h3>
              <button className="modal-close" onClick={() => setShowLinesModal(false)}>X</button>
            </div>
            <div className="modal-body">
              <div style={{ marginBottom: 16, padding: 12, background: '#f8fafc', borderRadius: 8 }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, fontSize: 13 }}>
                  <div><strong>Customer:</strong> {selectedInvoice.sell_to_customer_name}</div>
                  <div><strong>Posting Date:</strong> {formatDate(selectedInvoice.posting_date)}</div>
                  <div><strong>Total Amount:</strong> {formatCurrency(selectedInvoice.amount_including_vat)}</div>
                </div>
              </div>

              {linesLoading ? (
                <p style={{ textAlign: 'center', padding: 20 }}>Loading lines...</p>
              ) : invoiceLines.length === 0 ? (
                <p style={{ textAlign: 'center', padding: 20, color: '#64748b' }}>No invoice lines found</p>
              ) : (
                <div className="table-scroll">
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, minWidth: 500 }}>
                  <thead>
                    <tr style={{ background: '#f1f5f9' }}>
                      <th style={{ border: '1px solid #e2e8f0', padding: '8px 10px', textAlign: 'left' }}>Type</th>
                      <th style={{ border: '1px solid #e2e8f0', padding: '8px 10px', textAlign: 'left' }}>No.</th>
                      <th style={{ border: '1px solid #e2e8f0', padding: '8px 10px', textAlign: 'left' }}>Description</th>
                      <th style={{ border: '1px solid #e2e8f0', padding: '8px 10px', textAlign: 'right' }}>Quantity</th>
                      <th style={{ border: '1px solid #e2e8f0', padding: '8px 10px', textAlign: 'right' }}>Unit Price</th>
                      <th style={{ border: '1px solid #e2e8f0', padding: '8px 10px', textAlign: 'right' }}>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoiceLines.map((line, i) => (
                      <tr key={line.id || i} style={i % 2 === 0 ? { background: '#fafbfc' } : {}}>
                        <td style={{ border: '1px solid #e2e8f0', padding: '6px 10px' }}>{line.line_type || '-'}</td>
                        <td style={{ border: '1px solid #e2e8f0', padding: '6px 10px', fontFamily: 'monospace' }}>{line.item_no || '-'}</td>
                        <td style={{ border: '1px solid #e2e8f0', padding: '6px 10px', maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{line.description || '-'}</td>
                        <td style={{ border: '1px solid #e2e8f0', padding: '6px 10px', textAlign: 'right' }}>{line.quantity || 0}</td>
                        <td style={{ border: '1px solid #e2e8f0', padding: '6px 10px', textAlign: 'right' }}>{formatCurrency(line.unit_price)}</td>
                        <td style={{ border: '1px solid #e2e8f0', padding: '6px 10px', textAlign: 'right', fontWeight: 600 }}>{formatCurrency(line.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Edit Invoice Modal */}
      {editModal && (
        <div className="modal-overlay" onClick={() => setEditModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 560 }}>
            <div className="modal-header">
              <h3>Edit Invoice</h3>
              <button className="modal-close" onClick={() => setEditModal(null)}><X size={20} /></button>
            </div>
            <form onSubmit={handleSaveEdit}>
              <div className="modal-body">
                <div style={{ marginBottom: 16, padding: 12, background: '#f0f9ff', borderRadius: 8, fontSize: 13 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div>
                      <label style={{ fontSize: 11, color: '#64748b', fontWeight: 500 }}>Invoice Number</label>
                      <div style={{ fontWeight: 600, fontFamily: 'monospace', fontSize: 13 }}>{editModal.invoice_no || '-'}</div>
                    </div>
                    <div>
                      <label style={{ fontSize: 11, color: '#64748b', fontWeight: 500 }}>Amount</label>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{formatCurrency(editModal.amount_including_vat)}</div>
                    </div>
                  </div>
                </div>
                <div className="form-group" style={{ marginTop: 12 }}>
                  <label>Type</label>
                  <textarea
                    value={editForm.other_type}
                    onChange={e => setEditForm(prev => ({ ...prev, other_type: e.target.value }))}
                    rows={4}
                    placeholder="e.g., Service Charge, Discount, etc. Multiple lines supported."
                    style={{
                      width: '100%',
                      minHeight: '90px',
                      height: 'auto',
                      padding: '8px 12px',
                      border: '1px solid var(--border, #d1d5db)',
                      borderRadius: 6,
                      fontSize: 14,
                      fontFamily: 'inherit',
                      boxSizing: 'border-box',
                      resize: 'vertical',
                      display: 'block',
                      lineHeight: 1.5
                    }}
                  />
                </div>
                <div className="form-group" style={{ marginTop: 12 }}>
                  <label>Other Amounts</label>
                  <textarea
                    value={editForm.other_amounts}
                    onChange={e => setEditForm(prev => ({ ...prev, other_amounts: e.target.value }))}
                    rows={3}
                    placeholder="e.g., 1500.00, 200.00 (multiple lines supported)"
                    style={{
                      width: '100%',
                      minHeight: '70px',
                      height: 'auto',
                      padding: '8px 12px',
                      border: '1px solid var(--border, #d1d5db)',
                      borderRadius: 6,
                      fontSize: 14,
                      fontFamily: 'inherit',
                      boxSizing: 'border-box',
                      resize: 'vertical',
                      display: 'block',
                      lineHeight: 1.5
                    }}
                  />
                </div>
                <div className="form-group" style={{ marginTop: 12 }}>
                  <label>Other Remarks</label>
                  <textarea
                    value={editForm.other_remarks}
                    onChange={e => setEditForm(prev => ({ ...prev, other_remarks: e.target.value }))}
                    rows={3}
                    placeholder="Enter other remarks..."
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setEditModal(null)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving} style={{ background: '#3b82f6' }}>
                  <Save size={16} /> {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
